import { AnimatePresence, motion } from 'framer-motion';
import {
    Activity,
    AlertTriangle,
    Award,
    BookOpen,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Clock,
    Cpu,
    FileText,
    Maximize2,
    Menu,
    MessageSquare,
    PenTool,
    Radio,
    Sparkles,
    Target,
    Terminal,
    Trophy,
    Users,
    X
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePesaParcours } from '../../stores/usePesaParcours';
import CapstoneWarRoom from './CapstoneWarRoom';
import DualBuilder from './DualBuilder';
import NeuralNarrator from './NeuralNarrator';
import QuizEngine from './QuizEngine';
import SocialFieldMapper from './SocialFieldMapper';
import TableBuilder from './TableBuilder';
import VideoCallPage from './VideoCallPage';
import VisualComparator from './VisualComparator';


const LabActivityPage = () => {
  const { slug, activityId } = useParams();
  const navigate = useNavigate();
  const { 
    focusResource, validateGranule, validateEvidence, activeItem, harvestedData, 
    toggleShowNarrator, ui, closeNarrator, markActivityFailed, clearActivityFailed, 
    hasFailedActivity, initLabPreview, modules, resolveAsset 
  } = usePesaParcours();
  const showNarrator = ui.showNarrator;
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  const [showRightPanel, setShowRightPanel] = useState(false);
  const [theoryOpen, setTheoryOpen] = useState(true);
  const [practiceOpen, setPracticeOpen] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [showWritingZone, setShowWritingZone] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [startTime] = useState(Date.now());
  const [loadingError, setLoadingError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!activeItem && !loadingError) {
        setLoadingError(true);
        toast.error("L'activité n'a pas pu être chargée. Redirection vers l'investigation.");
        navigate(`/investigation/${slug}`);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [activeItem, loadingError, navigate, slug]);

  // Bootstrap initial (inchangé)
  useEffect(() => {
    const bootstrap = () => {
      let currentModules = modules;
      if (isPreview && modules.length === 0) {
        const rawData = localStorage.getItem('PESA_PREVIEW_DATA');
        if (rawData) {
          try {
            const parsed = JSON.parse(rawData);
            initLabPreview(parsed);
            currentModules = parsed.modules || [];
          } catch (e) {
            console.error("Échec parse Preview", e);
          }
        }
      }
      if (currentModules.length > 0 && activityId && (!activeItem || activeItem.id !== activityId)) {
        focusResource(activityId);
      }
    };
    bootstrap();
  }, [activityId, isPreview, modules.length, focusResource, activeItem]);

  const data = useMemo(() => activeItem?.data || {}, [activeItem]);
  const mediatorOnStart = data.mediatorOnStart || null;

  useEffect(() => {
    if (mediatorOnStart && !showNarrator) toggleShowNarrator();
  }, [mediatorOnStart, toggleShowNarrator]);

  const resources = data.resources || {};
  const fichier = data.fichier || null;
  const comprehensionQuiz = data.comprehensionQuiz || null;
  const liveSession = resources.liveSession || null;
  const objectives = Array.isArray(data.objectifs) ? data.objectifs : [];
  const steps = Array.isArray(resources.steps) ? resources.steps : [];
  const questions = Array.isArray(resources.questions) ? resources.questions : [];
  const fileUploads = Array.isArray(resources.fileUploads) ? resources.fileUploads : [];
  const validationSteps = Array.isArray(resources.validationSteps) ? resources.validationSteps : [];
  const centralWorkspace = data.centralWorkspace || null;
  const isCollaborative = data.groupCollaboration === true;
  const validationQuiz = validationSteps.find(s => s.type === 'quiz');

  const handleValidation = async () => {
    if (isPreview) {
      toast.info("Simulation : Données transmises avec succès.");
      navigate(`/investigation/${slug}`);
      return;
    }
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const expectedDuration = (data.duration || 60) * 60;
      await validateEvidence(activityId, { 
        type: 'activity', timeSpent, expectedDuration, score: 100, passed: true, pesaGain: data.points || 5 
      });
      toast.success("Activité validée !");
      navigate(`/investigation/${slug}`);
    } catch (err) {
      console.error("❌ Erreur validation:", err);
      toast.error("Erreur lors de la validation");
    }
  };

  const launchQuiz = (type, quizData, mediatorOnFail = null) => {
    const normalized = { 
      title: quizData.title || "Quiz", 
      passingScore: quizData.passingScore || 80, 
      questions: (quizData.questions || []).map((q, idx) => ({ 
        ...q, _id: q._id || `${type}-${idx}`, options: q.options || [] 
      })) 
    };
    setActiveQuiz({ type, data: normalized, mediatorOnFail });
  };

  const handleQuizComplete = async (passed = false, quizResults = {}) => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    const expectedDuration = (data.duration || 60) * 60;
    if (passed) {
      await validateEvidence(activityId, { 
        type: 'quiz', timeSpent, expectedDuration, 
        quizId: activeQuiz?.data?._id || `quiz-${activityId}`, 
        score: quizResults.score || 100, passed: true, answers: quizResults.answers || [] 
      });
      toast.success("Quiz réussi !");
      clearActivityFailed(activityId);
    } else {
      toast.error("Quiz non réussi – un chemin alternatif est disponible");
      markActivityFailed(activityId);
    }
    setActiveQuiz(null);
  };

  const formatQuizData = (quiz) => {
    if (!quiz || !quiz.questions) return null;
    return { 
      ...quiz, 
      questions: quiz.questions.map((q, idx) => ({ 
        ...q, _id: q._id || `q-${idx}`, 
        options: Array.isArray(q.options) 
          ? q.options.map((opt, optIdx) => ({ 
              text: typeof opt === 'string' ? opt : opt.text || '', 
              isCorrect: typeof opt === 'object' ? opt.isCorrect : q.correct === optIdx 
            })) 
          : [] 
      })) 
    };
  };

  const renderCentralWorkspace = () => {
    if (!centralWorkspace || centralWorkspace.type === "none") return null;
    switch (centralWorkspace.type) {
      case "visual-comparator": return <VisualComparator config={centralWorkspace.config} resolveAsset={resolveAsset} />;
      case "table-builder": return <TableBuilder config={centralWorkspace.config} previewData={centralWorkspace.previewData} />;
      case "dual-builder": return <DualBuilder config={centralWorkspace.config} />;
      case "social-field-mapper": return <SocialFieldMapper config={centralWorkspace.config} />;
      case "capstone-end": return <CapstoneWarRoom config={centralWorkspace.config} />;
      default: return null;
    }
  };

  const sortedReflections = Object.entries(harvestedData.reflections || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([stepId, value]) => ({ stepId, value }));

  if (!activeItem || !data) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#020617] text-orange-400">
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }} 
          className="w-16 h-16 border-2 border-orange-500/30 border-t-orange-500 rounded-full" 
        />
        <span className="ml-6 text-xs tracking-[0.4em] uppercase">
          {loadingError ? "Erreur de chargement..." : "Initialisation_Laboratoire"}
        </span>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden relative bg-[#020617] text-slate-100">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(#fb923c_1px,transparent_1px),linear-gradient(90deg,#fb923c_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

        {/* HEADER */}
    <header className="relative z-50 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-orange-500/10 bg-black/80 backdrop-blur-2xl">
    
    {/* Partie GAUCHE */}
    <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Hamburger - Mobile only */}
        <button
        onClick={() => setLeftPanelOpen(!leftPanelOpen)}
        className="lg:hidden p-2.5 rounded-xl border border-orange-500/20 bg-orange-500/5 text-orange-400 hover:bg-orange-500/20 transition-all flex-shrink-0"
        >
        <Menu size={22} />
        </button>

        {/* Bouton Back */}
        <motion.button 
        whileHover={{ scale: 1.05 }} 
        whileTap={{ scale: 0.95 }} 
        onClick={() => navigate(`/investigation/${slug}`)}
        className="p-2.5 rounded-xl border border-orange-500/20 bg-orange-500/5 text-orange-400 hover:bg-orange-500/20 transition-all flex-shrink-0"
        >
        <ChevronLeft size={20} />
        </motion.button>

        {/* Titre + Badge */}
        <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-orange-500/10 border border-orange-500/20 text-orange-400 text-[8px] uppercase tracking-[0.3em] whitespace-nowrap">
            <Terminal size={9} /> Lab_Protocol
            </span>
            {isCollaborative && (
            <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-purple-600/20 text-purple-400 border border-purple-500/30 text-[8px] uppercase tracking-widest whitespace-nowrap">
                <Users size={9} /> Collab
            </span>
            )}
        </div>
        <h1 className="text-base sm:text-lg font-black uppercase tracking-tight text-white truncate">
            {data.titre}
        </h1>
        </div>
    </div>

    {/* Partie DROITE - Actions */}
    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        
        {/* Duration + Points - adapté pour mobile : masque le texte sur xs */}
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest px-2 sm:px-3 py-1 rounded-full bg-white/5 border border-white/10">
        {data.duration > 0 && (
            <div className="flex items-center gap-1">
            <Clock size={12} className="text-orange-500" />
            {/* Texte masqué sur très petits écrans (moins de 480px) */}
            <span className="hidden xs:inline">{data.duration}m</span>
            </div>
        )}
        <div className="flex items-center gap-1 border-l border-white/10 pl-2">
            <Trophy size={12} className="text-emerald-400" />
            {/* Texte masqué sur très petits écrans */}
            <span className="hidden xs:inline text-emerald-400 font-black">+{data.points || 0}</span>
            {/* Variante : on peut afficher juste les points sans le "+" si besoin, mais ici on garde l'icône seule */}
        </div>
        </div>

        {/* Bouton Salle Collab - caché sur mobile (md:flex) */}
        {isCollaborative && (
        <motion.button 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/video-call?room=collab-${activityId}&activity=${slug}`)}
            className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full font-black uppercase text-[9px] tracking-wider bg-purple-600/20 border border-purple-500/50 text-purple-300 hover:bg-purple-600/40 transition-all"
        >
            <Users size={14} />
            <span className="hidden lg:inline">Salle collab</span>
        </motion.button>
        )}

        {/* Live Session - caché sur mobile (sm:flex) */}
        {liveSession && (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/20 border border-red-500/50 animate-pulse">
            <div className="relative">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
            <div className="absolute inset-0 w-2 h-2 bg-red-500 rounded-full" />
            </div>
            <div className="text-red-400 font-black text-[9px] uppercase tracking-wider">LIVE</div>
        </div>
        )}

        {/* Narrator - caché sur mobile (sm:flex) */}
        {mediatorOnStart && (
        <motion.button 
            onClick={toggleShowNarrator} 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl transition-all shadow-md"
            style={{ 
            background: showNarrator ? '#2563eb' : 'transparent', 
            border: showNarrator ? '1px solid #3b82f6' : '1px solid rgba(37,99,235,0.4)', 
            color: showNarrator ? '#fff' : '#60a5fa' 
            }}
        >
            <Radio size={14} className={showNarrator ? '' : 'animate-pulse'} />
        </motion.button>
        )}

        {/* Bouton Terminal / Right Panel - TOUJOURS VISIBLE et prioritaire */}
        <button 
        onClick={() => setShowRightPanel(!showRightPanel)}
        className="p-3 rounded-xl border border-orange-500/30 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 hover:border-orange-500 transition-all relative flex-shrink-0"
        >
        <Terminal size={19} />
        {!showRightPanel && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-ping" />
        )}
        </button>
    </div>
    </header>

      <main className="flex-1 flex flex-col lg:flex-row gap-3 p-3 lg:p-4 overflow-hidden relative z-10">

        {/* ====================== LEFT DIAGNOSTIC PANEL (Desktop + Drawer Mobile) ====================== */}
        {/* Version Desktop (visible en permanence sur lg+) */}
        <motion.aside 
            initial={false}
            animate={{ width: leftPanelOpen ? 320 : 48 }}
            className="hidden lg:flex shrink-0 flex-col gap-4 overflow-hidden transition-all duration-300"
        >
            <button
            onClick={() => setLeftPanelOpen(!leftPanelOpen)}
            className="absolute left-[308px] top-12 z-50 p-1.5 rounded-full border shadow-xl transition-all bg-slate-900 border-orange-500/30 text-orange-400 hover:bg-orange-500 hover:text-white"
            style={{ left: leftPanelOpen ? '308px' : '36px' }}
            >
            {leftPanelOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
            </button>

            <div className="flex-1 rounded-[2rem] border border-orange-500/20 bg-[#020617]/80 backdrop-blur-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-orange-500/10 bg-orange-500/5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                <Activity size={14} className="text-orange-400 animate-pulse" />
                {leftPanelOpen && (
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-orange-400">
                    Diagnostic_Système
                    </span>
                )}
                </div>
                {leftPanelOpen && (
                <div className="flex gap-1">
                    {[1, 2, 3].map(i => (
                    <div key={i} className={`w-1 h-1 rounded-full ${i === 1 ? 'bg-orange-500 animate-ping' : 'bg-orange-500/30'}`} />
                    ))}
                </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 space-y-6 min-h-0 no-scrollbar">
                {leftPanelOpen && (
                <>
                    {/* Progression théorique */}
                    <section>
                    <button 
                        onClick={() => setTheoryOpen(!theoryOpen)} 
                        className="w-full flex items-center justify-between mb-3 group"
                    >
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-white">
                        <BookOpen size={14} className="text-orange-400" />
                        Progression_Théorique
                        </h3>
                        {theoryOpen ? <ChevronUp size={14} className="text-orange-400" /> : <ChevronDown size={14} className="text-orange-400" />}
                    </button>
                    
                    <AnimatePresence>
                        {theoryOpen && sortedReflections.length > 0 && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            className="space-y-4"
                        >
                            {sortedReflections.map(({ stepId, value }, i) => (
                            <div key={stepId} className={`p-4 rounded-xl bg-white/[0.02] border border-white/5 ${i < sortedReflections.length - 1 ? 'mb-3' : ''}`}>
                                <p className="text-orange-400 text-[9px] uppercase mb-2">{stepId.replace(/[-_]/g, ' ')}</p>
                                {typeof value === 'string' && <p className="text-slate-300 text-xs leading-relaxed">{value}</p>}
                                {typeof value === 'object' && value !== null && (
                                <>
                                    {value.text && <p className="text-slate-300 text-xs leading-relaxed mb-3">{value.text}</p>}
                                    {value.mediaBase64 && (
                                    <div className="my-3">
                                        {value.mediaType?.includes('video') || value.mediaType === 'video' ? (
                                        <video controls src={value.mediaBase64} className="w-full rounded-lg shadow-lg max-h-[300px] object-contain" />
                                        ) : (
                                        <audio controls src={value.mediaBase64} className="w-full h-8" />
                                        )}
                                        <p className="text-[8px] text-slate-500 mt-1">Durée: {value.duree || '?'}s • {value.mediaType}</p>
                                    </div>
                                    )}
                                    {value.selected && (
                                    <div className="mt-3">
                                        <p className="text-orange-400 font-bold text-[9px] uppercase mb-2">Émotions</p>
                                        <div className="flex flex-wrap gap-1.5">
                                        {value.selected.map((emo, j) => (
                                            <span key={j} className="px-2 py-0.5 rounded-lg text-[9px] bg-orange-500/10 text-orange-300">
                                            {emo}
                                            </span>
                                        ))}
                                        </div>
                                        {value.custom && <p className="mt-2 text-xs italic text-slate-400">+ {value.custom}</p>}
                                    </div>
                                    )}
                                </>
                                )}
                            </div>
                            ))}
                        </motion.div>
                        )}
                    </AnimatePresence>
                    
                    {theoryOpen && sortedReflections.length === 0 && (
                        <p className="text-slate-500 text-xs italic text-center py-4">Aucune réflexion enregistrée.</p>
                    )}
                    </section>

                    {/* Action pratique */}
                    <section>
                    <button 
                        onClick={() => setPracticeOpen(!practiceOpen)} 
                        className="w-full flex items-center justify-between mb-3"
                    >
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-white">
                        <Target size={14} className="text-emerald-400" />
                        Action_Pratique
                        </h3>
                        {practiceOpen ? <ChevronUp size={14} className="text-emerald-400" /> : <ChevronDown size={14} className="text-emerald-400" />}
                    </button>
                    
                    <AnimatePresence>
                        {practiceOpen && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            className="space-y-4"
                        >
                            {objectives.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Objectifs</h4>
                                <ul className="space-y-2 text-xs text-slate-300">
                                {objectives.map((o, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                    <span className="text-orange-500 mt-0.5">•</span>
                                    <span>{o}</span>
                                    </li>
                                ))}
                                </ul>
                            </div>
                            )}
                            
                            {steps.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Étapes</h4>
                                <div className="space-y-2">
                                {steps.map((s, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                    <span className="text-orange-500 text-[9px] uppercase block mb-1">Étape_{i + 1}</span>
                                    <p className="text-xs text-slate-300">{s.title}</p>
                                    </div>
                                ))}
                                </div>
                            </div>
                            )}
                            
                            {questions.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Livrables</h4>
                                <div className="space-y-2">
                                {questions.map((q, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/10">
                                    <p className="text-xs italic text-orange-200">{q.text}</p>
                                    </div>
                                ))}
                                </div>
                            </div>
                            )}
                        </motion.div>
                        )}
                    </AnimatePresence>
                    </section>
                </>
                )}
            </div>
            </div>
        </motion.aside>

        {/* ====================== CENTRAL WORKSPACE ====================== */}
        <section className="flex-1 flex flex-col rounded-3xl lg:rounded-[2rem] border border-orange-500/20 bg-[#020617]/80 backdrop-blur-3xl shadow-2xl overflow-hidden">
            
            {/* Header Central Workspace */}
            <div className="px-5 sm:px-8 lg:px-10 py-4 sm:py-5 border-b border-orange-500/10 bg-gradient-to-b from-orange-500/[0.05] to-transparent flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-1.5 h-6 bg-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.5)] shrink-0" />
                
                <div className="min-w-0 flex-1">
                <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight italic text-white truncate">
                    Production_Hub
                </h2>
                <span className="text-[8px] tracking-[0.3em] text-orange-500/50 block mt-0.5">
                    SESSION_ID: {slug?.substring(0, 8)}
                </span>
                </div>
            </div>

            <motion.button 
                onClick={() => setShowWritingZone(!showWritingZone)} 
                whileHover={{ scale: 1.04 }} 
                whileTap={{ scale: 0.96 }}
                className="px-4 sm:px-5 py-2.5 rounded-xl text-[9px] sm:text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 whitespace-nowrap transition-all shadow-md shrink-0"
                style={{ 
                background: showWritingZone ? '#fb923c' : 'transparent', 
                border: showWritingZone ? '1px solid #fb923c' : '1px solid rgba(249,115,22,0.4)', 
                color: showWritingZone ? '#000' : '#fb923c' 
                }}
            >
                <PenTool size={13} className="shrink-0" />
                <span className="hidden xs:inline">
                {showWritingZone ? 'Fermer_Console' : 'Ouvrir_Console'}
                </span>
                <span className="xs:hidden">
                {showWritingZone ? 'Fermer' : 'Console'}
                </span>
            </motion.button>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto no-scrollbar p-6 sm:p-10">
            <div className={activeItem?.type === 'capstone' ? 'w-full' : 'max-w-6xl mx-auto'}>
                {renderCentralWorkspace() || (
                <>
                    <div className="text-center mt-24">
                    <Cpu size={60} className="mx-auto mb-6 text-orange-500/30" />
                    <p className="text-2xl font-black uppercase text-orange-400/70 tracking-[0.3em]">
                        Ouvrir_Console_Production
                    </p>
                    <p className="text-sm mt-4 max-w-xl mx-auto text-orange-400/50">
                        Le document de référence est disponible dans le panneau de droite.
                    </p>
                    </div>
                </>
                )}

                <AnimatePresence>
                {showWritingZone && (
                    <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: 20 }} 
                    className="mt-16"
                    >
                    <div className="rounded-3xl overflow-hidden shadow-2xl border border-orange-500/20 bg-black/40 backdrop-blur-xl">
                        <div className="p-6 border-b border-orange-500/10 bg-gradient-to-b from-orange-500/5 to-transparent">
                        <h3 className="text-lg font-black uppercase tracking-wider flex items-center gap-3 text-orange-400">
                            <PenTool size={18} />
                            Console_Rédaction
                        </h3>
                        <p className="text-[9px] mt-1 text-orange-500/70">Zone de production écrite — brouillons, analyses, rapports</p>
                        </div>
                        <div className="p-8">
                        <textarea 
                            className="w-full min-h-[500px] bg-transparent text-lg leading-relaxed focus:outline-none resize-none font-light text-white placeholder:text-white/20" 
                            placeholder="Commencer la rédaction..." 
                            style={{ caretColor: '#fb923c' }} 
                        />
                        </div>
                    </div>
                    </motion.div>
                )}
                </AnimatePresence>

                {fileUploads.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
                    {fileUploads.map((u, i) => (
                    <motion.div 
                        key={i} 
                        whileHover={{ scale: 1.02 }} 
                        className="relative p-8 rounded-[2rem] border border-white/5 bg-white/[0.02] hover:border-orange-500/40 transition-all overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/5 -mr-10 -mt-10 rotate-45" />
                        <h4 className="text-lg font-black mb-2 tracking-tight uppercase text-white">{u.label}</h4>
                        <p className="text-[9px] mb-6 uppercase tracking-widest italic text-orange-500">
                        {u.accept} // {u.maxSize}MB_MAX
                        </p>
                        <button className="w-full py-3 bg-orange-500/10 hover:bg-orange-500 hover:text-black border border-orange-500/20 rounded-xl font-black uppercase text-[10px] tracking-widest text-orange-400 transition-all">
                        Initialize_Upload
                        </button>
                    </motion.div>
                    ))}
                </div>
                )}
            </div>
            </div>

            {/* Footer */}
            <div className="h-14 sm:h-16 px-6 sm:px-10 border-t border-orange-500/10 bg-[#020617]/70 backdrop-blur-xl flex items-center justify-between">
            <div className="flex items-center gap-4">
                <div className="flex gap-1">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-1 h-2.5" style={{ background: i < 4 ? '#fb923c' : 'rgba(255,255,255,0.1)' }} />
                ))}
                </div>
                <p className="text-[8px] uppercase tracking-[0.2em] text-orange-500/70">Terminal_Ready</p>
            </div>

            <button 
                onClick={handleValidation}
                className="group relative px-8 py-2.5 rounded-lg overflow-hidden border border-orange-500/30 hover:border-orange-400 transition-all"
            >
                <div className="absolute inset-0 bg-orange-500/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <div className="relative flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-400 group-hover:text-orange-50">
                    Transmettre_Data
                </span>
                <ChevronRight size={12} className="text-orange-500 group-hover:translate-x-1 transition-transform" />
                </div>
            </button>
            </div>
        </section>

        {/* ====================== RIGHT CONTROL PANEL ====================== */}
        <AnimatePresence>
            {showRightPanel && (
            <motion.aside 
                initial={{ x: '100%', opacity: 0 }} 
                animate={{ x: 0, opacity: 1 }} 
                exit={{ x: '100%', opacity: 0 }}
                className="fixed lg:static inset-y-0 right-0 w-full lg:w-80 z-[100] lg:z-50 rounded-t-3xl lg:rounded-[2rem] border border-orange-500/20 bg-[#020617]/95 backdrop-blur-3xl shadow-2xl overflow-hidden"
            >
                <div className="p-5 border-b border-orange-500/10 bg-orange-500/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Activity size={14} className="text-orange-400" />
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-orange-400">Control_Panel</span>
                </div>
                <div className="flex gap-1">
                    <div className="w-1 h-1 rounded-full bg-orange-500 animate-ping" />
                    <div className="w-1 h-1 rounded-full bg-orange-500/40" />
                </div>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar p-5 space-y-6">
                {fichier && (
                    <section className="rounded-2xl overflow-hidden bg-black/20 border border-white/5">
                    <div className="w-full p-4 flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
                        <button 
                        onClick={() => setIsPdfOpen(!isPdfOpen)}
                        className="flex items-center gap-2 flex-grow text-left"
                        >
                        <FileText size={12} className="text-amber-500" />
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Documentation</span>
                        {isPdfOpen ? <ChevronUp size={12} className="text-slate-500" /> : <ChevronDown size={12} className="text-slate-500" />}
                        </button>

                        <a 
                        href={resolveAsset(fichier.path)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 p-1.5 rounded-md bg-white/5 hover:bg-amber-500/20 hover:text-amber-500 transition-all group"
                        title="Ouvrir en plein écran"
                        >
                        <Maximize2 size={14} />
                        </a>
                    </div>
                    
                    <AnimatePresence>
                        {isPdfOpen && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }} 
                            animate={{ height: 280, opacity: 1 }} 
                            exit={{ height: 0, opacity: 0 }} 
                            className="relative overflow-hidden"
                        >
                            <iframe 
                            key={fichier.path}
                            title="Visionneuse PDF"
                            src={`${resolveAsset(fichier.path)}#toolbar=0&view=FitH&navpanes=0&scrollbar=0`} 
                            className="w-full h-full opacity-70 border-none" 
                            style={{ border: 'none' }}
                            />
                            <div className="absolute inset-0 pointer-events-none h-10 w-full animate-scan bg-gradient-to-b from-transparent via-orange-500/5 to-transparent" />
                        </motion.div>
                        )}
                    </AnimatePresence>
                    </section>
                )}

                {data.realTime?.enabled && (
                    <div>
                    <h4 className="text-[9px] font-black uppercase mb-3 flex items-center gap-2 text-orange-400">
                        <MessageSquare size={12} />
                        Mur_Vivant
                    </h4>
                    <div className="p-5 rounded-xl bg-orange-500/5 border border-orange-500/20">
                        <p className="text-xs mb-4 text-slate-300">
                        {data.realTime.wallDescription || 'Partagez vos observations en temps réel.'}
                        </p>
                        <button 
                        onClick={() => { focusResource(activityId); navigate(`/wall/${activityId}`); }}
                        className="w-full py-3 rounded-xl font-black uppercase text-[9px] tracking-wider bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/50 transition-all"
                        >
                        Accéder_Mur
                        </button>
                        {data.realTime.flashChallenge?.active && (
                        <div className="mt-3 p-3 rounded-lg bg-orange-600/20 border border-orange-500/40">
                            <p className="text-[9px] font-black uppercase text-orange-300">
                            Défi: {data.realTime.flashChallenge.title}
                            </p>
                            <p className="text-[8px] mt-1 text-orange-400">
                            +{data.realTime.flashChallenge.bonusPoints} pts
                            </p>
                        </div>
                        )}
                    </div>
                    </div>
                )}

                <section className="space-y-3">
                    <p className="text-[8px] font-black uppercase tracking-[0.3em] text-white/30">Validation_Module</p>

                    {comprehensionQuiz && (
                    <div 
                        className="group p-4 rounded-xl transition-all cursor-pointer bg-white/[0.02] border border-white/5 hover:bg-purple-500/[0.05] hover:border-purple-500/30"
                        onClick={() => launchQuiz('comprehension', comprehensionQuiz)}
                    >
                        <div className="flex justify-between items-start mb-3">
                        <div className="p-2 rounded-lg bg-purple-500/10">
                            <Sparkles size={16} className="text-purple-400" />
                        </div>
                        <div className="text-right">
                            <span className="text-[8px] block text-purple-400/50">SCORE_MIN</span>
                            <span className="text-xs font-black text-white">{comprehensionQuiz.passingScore || 80}%</span>
                        </div>
                        </div>
                        <h4 className="text-[9px] font-black uppercase tracking-widest mb-2 text-slate-300">
                        {comprehensionQuiz.title}
                        </h4>
                        <div className="flex gap-1 h-1">
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="flex-1 rounded-sm transition-colors duration-500" 
                            style={{ background: i < 3 ? 'rgba(147,51,234,0.4)' : 'rgba(255,255,255,0.05)' }} 
                            />
                        ))}
                        </div>
                    </div>
                    )}

                    {validationQuiz && (
                    <div 
                        className="group p-4 rounded-xl transition-all cursor-pointer bg-white/[0.02] border border-white/5 hover:bg-emerald-500/[0.05] hover:border-emerald-500/30"
                        onClick={() => launchQuiz('validation', formatQuizData(validationQuiz))}
                    >
                        <div className="flex justify-between items-start mb-3">
                        <div className="p-2 rounded-lg bg-emerald-500/10">
                            <Award size={16} className="text-emerald-400" />
                        </div>
                        <span className="text-[8px] text-emerald-400/50">READY_TO_SCAN</span>
                        </div>
                        <h4 className="text-[9px] font-black uppercase tracking-widest mb-2 text-slate-300">
                        {validationQuiz.title}
                        </h4>
                        <div className="flex gap-1 h-1">
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="flex-1 rounded-sm bg-white/5" />
                        ))}
                        </div>
                    </div>
                    )}
                </section>

                {data.alternativePath && hasFailedActivity(activityId) && (
                    <div className="p-5 rounded-2xl bg-amber-600/20 border border-amber-500/40 animate-pulse shadow-lg">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-amber-400" />
                        <h4 className="text-xs font-black uppercase text-amber-300">Chemin_Alternatif</h4>
                    </div>
                    <p className="text-[10px] leading-relaxed mb-4 text-slate-300">
                        {data.alternativePath.description}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        <button 
                        onClick={async () => {
                            toast.success("Chemin alternatif validé");
                            await validateGranule(activityId, { pesaGain: Math.round((data.points || 5) * 0.7), alternativePath: true });
                            clearActivityFailed(activityId);
                            navigate(`/investigation/${slug}`);
                        }} 
                        className="py-2.5 rounded-xl font-black uppercase text-[9px] tracking-wider bg-amber-600/50 hover:bg-amber-600/70 border border-amber-500/70 transition-all"
                        >
                        Valider (70%)
                        </button>
                        <button 
                        onClick={() => {
                            clearActivityFailed(activityId);
                            toast.info("Réessayez le quiz");
                        }} 
                        className="py-2.5 rounded-xl font-black uppercase text-[9px] tracking-wider bg-white/5 hover:bg-white/10 border border-white/20 transition-all"
                        >
                        Réessayer
                        </button>
                    </div>
                    </div>
                )}

                {liveSession && (
                    <motion.div 
                    whileHover={{ scale: 1.03 }} 
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate(`/video-call?room=live-${activityId}&title=${encodeURIComponent(liveSession.title)}&date=${liveSession.date}`)}
                    className="relative p-5 rounded-2xl cursor-pointer overflow-hidden shadow-2xl border-2 border-red-500/50 bg-gradient-to-br from-red-600/20 to-red-900/10"
                    >
                    <div className="absolute inset-0 bg-red-500/10 animate-ping" />
                    <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-red-600/30 border-4 border-red-500/60 flex items-center justify-center">
                            <div className="w-4 h-4 bg-red-500 rounded-full animate-ping" />
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-black uppercase tracking-tight text-red-300">Live_Officiel</p>
                            <p className="text-[10px] mt-0.5 text-red-400">{liveSession.date} à {liveSession.time}</p>
                        </div>
                        </div>
                        <ChevronRight size={18} className="text-red-400" />
                    </div>
                    <div className="absolute top-3 right-3 px-2.5 py-1 text-[8px] font-black uppercase rounded-full bg-red-700 text-white animate-pulse">
                        ON_AIR
                    </div>
                    </motion.div>
                )}
                </div>

                <div className="p-4 bg-black/40 border-t border-white/5">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                    <span className="text-[7px] uppercase font-black text-slate-600">Data_Integrity</span>
                    <div className="h-1 rounded-full overflow-hidden bg-white/5">
                        <motion.div initial={{ width: 0 }} animate={{ width: '94%' }} className="h-full bg-orange-400/40" />
                    </div>
                    </div>
                    <div className="space-y-1">
                    <span className="text-[7px] uppercase font-black text-slate-600">Sync_Rate</span>
                    <div className="h-1 rounded-full overflow-hidden bg-white/5">
                        <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} className="h-full bg-emerald-500/40" />
                    </div>
                    </div>
                </div>
                </div>

                {/* Bouton Fermer sur mobile uniquement */}
                <div className="lg:hidden p-4 border-t border-orange-500/10 flex justify-end">
                <button onClick={() => setShowRightPanel(false)} className="text-orange-400 font-medium">
                    Fermer
                </button>
                </div>
            </motion.aside>
            )}
        </AnimatePresence>

        {/* Drawer Left Panel - Mobile uniquement */}
        <AnimatePresence>
            {leftPanelOpen && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[90] lg:hidden bg-black/70 backdrop-blur-sm"
                onClick={() => setLeftPanelOpen(false)}
            >
                <motion.aside 
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="w-[92%] max-w-[380px] h-full bg-[#020617] border-r border-orange-500/20 overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                >
                {/* Header du drawer */}
                <div className="px-5 py-4 border-b border-orange-500/10 bg-orange-500/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                    <Activity size={16} className="text-orange-400" />
                    <span className="text-sm font-black uppercase tracking-widest text-orange-400">
                        Diagnostic_Système
                    </span>
                    </div>
                    <button 
                    onClick={() => setLeftPanelOpen(false)} 
                    className="text-orange-400"
                    >
                    <X size={20} />   {/* ← Tu dois importer X de lucide-react */}
                    </button>
                </div>

                {/* Contenu identique au desktop */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 space-y-6 min-h-0 no-scrollbar">
                {leftPanelOpen && (
                <>
                    {/* Progression théorique */}
                    <section>
                    <button 
                        onClick={() => setTheoryOpen(!theoryOpen)} 
                        className="w-full flex items-center justify-between mb-3 group"
                    >
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-white">
                        <BookOpen size={14} className="text-orange-400" />
                        Progression_Théorique
                        </h3>
                        {theoryOpen ? <ChevronUp size={14} className="text-orange-400" /> : <ChevronDown size={14} className="text-orange-400" />}
                    </button>
                    
                    <AnimatePresence>
                        {theoryOpen && sortedReflections.length > 0 && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            className="space-y-4"
                        >
                            {sortedReflections.map(({ stepId, value }, i) => (
                            <div key={stepId} className={`p-4 rounded-xl bg-white/[0.02] border border-white/5 ${i < sortedReflections.length - 1 ? 'mb-3' : ''}`}>
                                <p className="text-orange-400 text-[9px] uppercase mb-2">{stepId.replace(/[-_]/g, ' ')}</p>
                                {typeof value === 'string' && <p className="text-slate-300 text-xs leading-relaxed">{value}</p>}
                                {typeof value === 'object' && value !== null && (
                                <>
                                    {value.text && <p className="text-slate-300 text-xs leading-relaxed mb-3">{value.text}</p>}
                                    {value.mediaBase64 && (
                                    <div className="my-3">
                                        {value.mediaType?.includes('video') || value.mediaType === 'video' ? (
                                        <video controls src={value.mediaBase64} className="w-full rounded-lg shadow-lg max-h-[300px] object-contain" />
                                        ) : (
                                        <audio controls src={value.mediaBase64} className="w-full h-8" />
                                        )}
                                        <p className="text-[8px] text-slate-500 mt-1">Durée: {value.duree || '?'}s • {value.mediaType}</p>
                                    </div>
                                    )}
                                    {value.selected && (
                                    <div className="mt-3">
                                        <p className="text-orange-400 font-bold text-[9px] uppercase mb-2">Émotions</p>
                                        <div className="flex flex-wrap gap-1.5">
                                        {value.selected.map((emo, j) => (
                                            <span key={j} className="px-2 py-0.5 rounded-lg text-[9px] bg-orange-500/10 text-orange-300">
                                            {emo}
                                            </span>
                                        ))}
                                        </div>
                                        {value.custom && <p className="mt-2 text-xs italic text-slate-400">+ {value.custom}</p>}
                                    </div>
                                    )}
                                </>
                                )}
                            </div>
                            ))}
                        </motion.div>
                        )}
                    </AnimatePresence>
                    
                    {theoryOpen && sortedReflections.length === 0 && (
                        <p className="text-slate-500 text-xs italic text-center py-4">Aucune réflexion enregistrée.</p>
                    )}
                    </section>

                    {/* Action pratique */}
                    <section>
                    <button 
                        onClick={() => setPracticeOpen(!practiceOpen)} 
                        className="w-full flex items-center justify-between mb-3"
                    >
                        <h3 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-white">
                        <Target size={14} className="text-emerald-400" />
                        Action_Pratique
                        </h3>
                        {practiceOpen ? <ChevronUp size={14} className="text-emerald-400" /> : <ChevronDown size={14} className="text-emerald-400" />}
                    </button>
                    
                    <AnimatePresence>
                        {practiceOpen && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }} 
                            className="space-y-4"
                        >
                            {objectives.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Objectifs</h4>
                                <ul className="space-y-2 text-xs text-slate-300">
                                {objectives.map((o, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                    <span className="text-orange-500 mt-0.5">•</span>
                                    <span>{o}</span>
                                    </li>
                                ))}
                                </ul>
                            </div>
                            )}
                            
                            {steps.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Étapes</h4>
                                <div className="space-y-2">
                                {steps.map((s, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                    <span className="text-orange-500 text-[9px] uppercase block mb-1">Étape_{i + 1}</span>
                                    <p className="text-xs text-slate-300">{s.title}</p>
                                    </div>
                                ))}
                                </div>
                            </div>
                            )}
                            
                            {questions.length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-black uppercase mb-2 text-orange-400">Livrables</h4>
                                <div className="space-y-2">
                                {questions.map((q, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/10">
                                    <p className="text-xs italic text-orange-200">{q.text}</p>
                                    </div>
                                ))}
                                </div>
                            </div>
                            )}
                        </motion.div>
                        )}
                    </AnimatePresence>
                    </section>
                </>
                )}
            </div>
                </motion.aside>
            </motion.div>
            )}
        </AnimatePresence>

        </main>

      

      <AnimatePresence>
        {activeQuiz && (
          <QuizEngine 
            quizData={activeQuiz.data} 
            mode={activeQuiz.type} 
            mediatorOnFail={activeQuiz.mediatorOnFail}
            onComplete={() => handleQuizComplete(true)} 
            onFail={() => handleQuizComplete(false)} 
            onClose={() => setActiveQuiz(null)} 
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showVideoCall && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80" 
            onClick={() => setShowVideoCall(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full h-full max-w-7xl max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl" 
              onClick={(e) => e.stopPropagation()}
            >
              <VideoCallPage onClose={() => setShowVideoCall(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNarrator && mediatorOnStart && (
          <NeuralNarrator 
            isOpen={showNarrator} 
            onClose={closeNarrator} 
            mediators={mediatorOnStart} 
            resolveAsset={resolveAsset} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LabActivityPage;